import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { User, Mail, Phone, LogOut, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

interface BasicInfoTabProps {
  profile?: {
    id: string;
    name: string;
    email: string;
    phone_number: string;
    avatar_url: string;
  };
  isLoading?: boolean;
}

export const BasicInfoTab = ({ profile, isLoading }: BasicInfoTabProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: profile?.name || '',
    email: profile?.email || '',
    phone_number: profile?.phone_number || '',
  });
  const [originalPhone, setOriginalPhone] = useState(profile?.phone_number || '');

  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Update form data when profile prop changes
  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        email: profile.email || '',
        phone_number: profile.phone_number || '',
      });
      setOriginalPhone(profile.phone_number || '');
    }
  }, [profile]);

  const handleEdit = () => {
    setFormData({
      name: profile?.name || '',
      email: profile?.email || '',
      phone_number: profile?.phone_number || '',
    });
    setOriginalPhone(profile?.phone_number || '');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFormData({
      name: profile?.name || '',
      email: profile?.email || '',
      phone_number: profile?.phone_number || '',
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    // Only show phone change dialog if:
    // 1. There was an original phone number AND
    // 2. The phone number has changed
    // This prevents the dialog from showing when setting phone for the first time
    const phoneChanged = originalPhone && formData.phone_number !== originalPhone;
    
    if (phoneChanged) {
      setShowPhoneDialog(true);
      return;
    }

    await updateProfileWithPhone();
  };

  const updateProfileWithPhone = async () => {
    if (!profile?.id) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          name: formData.name,
          email: formData.email,
          phone_number: formData.phone_number,
        })
        .eq('id', profile.id);

      if (error) throw error;

      // Update original phone after successful save
      setOriginalPhone(formData.phone_number);

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['profile-complete'] });
      
      toast({
        title: "Profile updated",
        description: "Your profile information has been updated successfully.",
      });
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhoneChange = async () => {
    if (!profile?.id) return;

    setIsSaving(true);
    try {
      // Check if phone number exists
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('phone_number', formData.phone_number)
        .neq('id', profile.id)
        .single();

      if (existingProfile) {
        toast({
          title: "Phone number already exists",
          description: "This phone number is already associated with another account.",
          variant: "destructive",
        });
        return;
      }

      // Update profile with new phone number
      const { error } = await supabase
        .from('profiles')
        .update({
          name: formData.name,
          email: formData.email,
          phone_number: formData.phone_number,
        })
        .eq('id', profile.id);

      if (error) throw error;

      // Sign out user for re-authentication
      await supabase.auth.signOut();
      
      toast({
        title: "Phone number updated",
        description: "Please sign in again with your new phone number.",
      });
      
      navigate('/auth');
    } catch (error) {
      console.error('Error updating phone:', error);
      toast({
        title: "Error",
        description: "Failed to update phone number. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
      setShowPhoneDialog(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };

  const getInitials = (name?: string) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center space-x-4">
            <div className="h-20 w-20 rounded-full bg-muted animate-pulse" />
            <div className="space-y-2 mb-8">
              <div className="h-4 w-32 bg-muted animate-pulse rounded" />
              <div className="h-3 w-48 bg-muted animate-pulse rounded" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Basic Information</CardTitle>
          <div className="space-x-2">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <Button onClick={handleEdit}>
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar Section */}
          <div className="flex items-center space-x-4">
            <Avatar className="h-20 w-20">
              <AvatarImage src={profile?.avatar_url} className="object-cover" />
              <AvatarFallback className="text-lg font-semibold">
                {getInitials(profile?.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-lg font-semibold">{profile?.name || 'No name set'}</h3>
              <p className="text-sm text-muted-foreground">Profile picture</p>
            </div>
          </div>

          {/* Form Fields */}
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                <User className="h-4 w-4 inline mr-2" />
                Full Name
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                disabled={!isEditing}
                placeholder="Enter your full name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                <Mail className="h-4 w-4 inline mr-2" />
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                disabled={!isEditing}
                placeholder="Enter your email address"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">
                <Phone className="h-4 w-4 inline mr-2" />
                Phone Number
              </Label>
              <Input
                id="phone"
                value={formData.phone_number}
                onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                disabled={!isEditing}
                placeholder="Enter your phone number"
              />
              {isEditing && (
                <p className="text-sm text-muted-foreground">
                  Changing your phone number will require you to sign in again.
                </p>
              )}
            </div>
          </div>

          {/* Sign Out Button */}
          <div className="pt-4 border-t">
            <Button variant="outline" onClick={handleSignOut} className="w-full md:w-auto">
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Phone Change Confirmation Dialog */}
      <AlertDialog open={showPhoneDialog} onOpenChange={setShowPhoneDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Phone Number Change</AlertDialogTitle>
            <AlertDialogDescription>
              Changing your phone number will sign you out and require you to sign in again with the new number. 
              Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePhoneChange} disabled={isSaving}>
              {isSaving ? 'Updating...' : 'Continue'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};